import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import {
  ChevronDownIcon,
  GridIcon,
  HorizontaLDots,
  LockIcon,
  PlugInIcon,
} from "../icons";
import { ChefHat, CalendarDays, ShoppingCart, Phone, Palette, FileText, Shield } from "lucide-react";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";

import CallModal from "../components/calls/CallModal";

import { useSidebar } from "../context/SidebarContext";
import SidebarWidget from "./SidebarWidget";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useFamilyMode } from "../context/FamilyModeContext";
import { useToast } from "../components/ui/toast/ToastProvider";
import { useActiveFamily } from "../context/ActiveFamilyContext";
import { signOut } from "firebase/auth";
import { auth } from "../firebase/firebaseConfig";
import { navigateTo } from "../utils/navigation";
import { useMemberRole } from "../hooks/useMemberRole";
import FamilySwitcher from "../components/groups/FamilySwitcher";
import { useNotifications } from "../hooks/useNotifications";
import { usePendingRedemptions } from "../hooks/useRedemptions";
import { Capacitor } from "@capacitor/core";
import ContextualFAB from "../components/navigation/ContextualFAB";
import { useContextualCopys } from "../hooks/useContextualCopys";
import { useFeatureAccess } from "../hooks/useFeatureAccess";
import { usePlanLimits } from "../hooks/usePlanLimits";
import { useFeatureGate } from "../hooks/useFeatureGate";
import { FeatureBadge, getBadgeVariantFromLimit } from "../components/subscription/FeatureBadge";
import { UpgradePlanModal } from "../components/subscription/UpgradePlanModal";
import UpgradeHint from "../components/ui/UpgradeHint";
import { useFeatureUnlock } from "../hooks/useFeatureUnlock";
import { FeatureInvitationBanner } from "../components/features/FeatureInvitationBanner";
import { getPrioritizedBanners } from "../utils/featurePriority";
import ContactSupportModal from "../components/support/ContactSupportModal";
import { useHasFamily } from "../hooks/useHasFamily";

type SubItem = {
  name: string;
  path: string;
  pro?: boolean;
};

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: SubItem[];
};

type CurrentMemberData = {
  avatar: string | null;
  name: string | null;
};

function useCurrentMember(memberId: string | null) {
  const [memberData, setMemberData] = useState<CurrentMemberData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadMember = async () => {
      if (!memberId) {
        if (isMounted) {
          setMemberData(null);
        }
        return;
      }

      setLoading(true);
      try {
        const memberRef = doc(db, "members", memberId);
        const snapshot = await getDoc(memberRef);
        if (!isMounted) return;

        if (snapshot.exists()) {
          const data = snapshot.data();
          setMemberData({
            avatar: (data?.avatar as string) || null,
            name: (data?.name as string) || null,
          });
        } else {
          setMemberData(null);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error loading current member:", error);
          setMemberData(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadMember();

    return () => {
      isMounted = false;
    };
  }, [memberId]);

  return { memberData, loading };
}

function AppSidebar() {
  const { t } = useTranslation();

  const { isMobileOpen, closeMobileSidebar, toggleMobileSidebar } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { enabled: familyMode, enable: enableFamilyMode, openExitModal} = useFamilyMode();
  const { showToast } = useToast();
  const { isMember, isOwnerOrAdmin, loading: memberRoleLoading, memberId } = useMemberRole();
  const { memberData: currentMemberData } = useCurrentMember(memberId);
  const { unreadCount } = useNotifications();
  const { activeFamily } = useActiveFamily();
  const { count: pendingRedemptionsCount } = usePendingRedemptions(activeFamily?.id || null);
  const copys = useContextualCopys(); // Copys contextuales según tipo de grupo
  const { 
    canAccessTemplates, 
    canAccessEvaluations, 
    canAccessFamilyStats,
    isEducacion 
  } = useFeatureAccess(); // Feature gating por licencia
  const { isFeatureBlocked, planInfo, isTrialActive } = usePlanLimits(); // Verificar límites del plan
  const { 
    showUpgradeModal, 
    blockedFeatureInfo, 
    closeUpgradeModal, 
    promptUpgrade,
    checkFeatureAccess
  } = useFeatureGate(); // Feature gating con modal de upgrade
  const { features, metrics, unlockFeature } = useFeatureUnlock(); // Sistema modular de features
  const { hasFamily, loading: hasFamilyLoading } = useHasFamily(); // Detectar si tiene familia
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userDoc, setUserDoc] = useState<any | null>(null);
  const [userDocLoading, setUserDocLoading] = useState(true);
  const [marketplaceEnabled, setMarketplaceEnabled] = useState(true);
  const [templateLibraryEnabled, setTemplateLibraryEnabled] = useState(true);
  const [showContactSupport, setShowContactSupport] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);

  const isCallsBlocked = isFeatureBlocked('familyCalls' as any);

  const openCalls = () => {
    if (isCallsBlocked) {
      promptUpgrade(
        'Llamadas',
        'Accede al sistema de llamadas familiares (Casa / Miembro).'
      );
      return;
    }
    if (isMobileOpen) {
      closeMobileSidebar();
    }
    setCallModalOpen(true);
  };

  // Estado para mantener el modal visible durante la animación
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  // Manejar apertura/cierre con animación
  useEffect(() => {
    if (isMobileOpen && !showMobileModal) {
      // Abrir: primero mostrar fuera de vista, luego animar entrada
      setShowMobileModal(true);
      setIsAnimatingIn(true);
      setIsAnimatingOut(false);
      // Después de un frame, iniciar animación de entrada
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimatingIn(false);
        });
      });
    } else if (!isMobileOpen && showMobileModal) {
      // Cerrar: animar salida y luego ocultar
      setIsAnimatingOut(true);
      setIsAnimatingIn(false);
      const timer = setTimeout(() => {
        setShowMobileModal(false);
        setIsAnimatingOut(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isMobileOpen, showMobileModal]);

  const shouldRenderMobile = showMobileModal;

  const navigateToPath = useCallback((path: string) => {
    // Cerrar sidebar PRIMERO para transición fluida
    closeMobileSidebar();
    // Esperar un frame para que el sidebar comience a cerrarse
    requestAnimationFrame(() => {
      if (isMobileOpen) {
        sessionStorage.setItem('came_from_sidebar', 'true');
      }
      navigate(path);
    });
  }, [navigate, closeMobileSidebar, isMobileOpen]);

  // Helper para cerrar sidebar mobile y marcar que venimos del sidebar
  const handleMobileLinkClick = useCallback(() => {
    closeMobileSidebar();
    if (isMobileOpen) {
      sessionStorage.setItem('came_from_sidebar', 'true');
    }
  }, [isMobileOpen, closeMobileSidebar]);

  // Leer documento de usuario (rol y otros datos)
  useEffect(() => {
    if (!user?.uid) {
      setUserDoc(null);
      setUserRole(null);
      setUserDocLoading(false);
      return;
    }
    
    setUserDocLoading(true);
    const unsub = onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setUserDoc(data);
          setUserRole(data?.role || null);
        } else {
          
          setUserDoc(null);
          setUserRole(null);
        }
        setUserDocLoading(false);
      },
      (error) => {
        
        setUserDoc(null);
        setUserRole(null);
        setUserDocLoading(false);
      }
    );
    return () => unsub();
  }, [user?.uid]);

  // Leer configuración del marketplace de la familia activa
  useEffect(() => {
    if (!activeFamily?.id) {
      setMarketplaceEnabled(true);
      return;
    }
    
    const unsub = onSnapshot(
      doc(db, 'families', activeFamily.id),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          // Por defecto está habilitado si no se especifica
          setMarketplaceEnabled(data?.marketplaceEnabled !== undefined ? data.marketplaceEnabled : true);
        } else {
          setMarketplaceEnabled(true);
        }
      },
      () => {
        setMarketplaceEnabled(true);
      }
    );
    return () => unsub();
  }, [activeFamily?.id]);

  // Verificar si template_library está habilitado como feature
  useEffect(() => {
    if (!user?.uid) {
      setTemplateLibraryEnabled(true);
      return;
    }
    
    (async () => {
      try {
        const { canUse } = await import('../services/featureGate');
        const hasAccess = await canUse(user.uid, 'template_library');
        setTemplateLibraryEnabled(hasAccess);
      } catch {
        setTemplateLibraryEnabled(true); // Por defecto habilitado si hay error
      }
    })();
  }, [user?.uid]);

  // Items del menú filtrados según rol
  const dashboardItems: NavItem[] = useMemo(() => {
    // No evaluar hasta que todo esté cargado
    if (memberRoleLoading || userDocLoading || hasFamilyLoading) {
      return [];
    }

    const familyToolsSubItems = [
      features?.mealPlanCalendar?.enabled ? { name: t('family.mealPlannerShort', { defaultValue: 'Planificador' }), path: "/family/meal-planner", pro: false } : null,
      features?.shoppingList?.enabled ? { name: t('family.shoppingList', { defaultValue: 'Lista de Compras' }), path: "/family/shopping-list", pro: false } : null,
    ].filter(Boolean) as { name: string; path: string; pro: boolean }[];

    const familyToolsNav: NavItem | null =
      activeFamily?.licenseType === 'familia' && familyToolsSubItems.length
        ? {
            icon: <ChefHat className="w-5 h-5" />,
            name: t('common.family', { defaultValue: 'Familia' }),
            subItems: familyToolsSubItems,
          }
        : null;
    
    // Si no tiene familia, mostrar solo Tablero personal (con empty state + CTA adentro)
    if (hasFamily === false) {
      return [
        {
          icon: <GridIcon />,
          name: t('common.tableropersonal'),
          subItems: [
            { name: t('common.tableropersonal'), path: "/groups/dashboard", pro: false },
          ],
        },
      ];
    }
    
    if (isMember) {
      // Members solo ven "Inicio de grupos"
      return [
        {
          icon: <GridIcon />,
          name: t('common.tableropersonal'),
          subItems: [
            { name: t('common.tableropersonal'), path: "/groups/home-groups", pro: false },
          ],
        },
        ...(familyToolsNav ? [familyToolsNav] : []),
      ];
    }
    
    // Owner/Admin ven todo
    return [
      {
        icon: <GridIcon />,
        name: t('common.tableropersonal'),
        subItems: [
          { name: t('common.tableropersonal'), path: "/groups/dashboard", pro: false },
          { name: t('common.tablerogrupal'), path: "/groups/home-groups", pro: false },
        ],
      },
      ...(familyToolsNav ? [familyToolsNav] : []),
    ];
  }, [isMember, memberRoleLoading, userDocLoading, hasFamilyLoading, hasFamily, t, activeFamily?.licenseType, features?.advancedRecipes?.enabled, features?.mealPlanCalendar?.enabled, features?.shoppingList?.enabled]);

  const organizacionItems: NavItem[] = useMemo(() => {
    // No evaluar permisos hasta que todo esté cargado
    if (memberRoleLoading || userDocLoading || hasFamilyLoading) {
      return [];
    }
    
    // Si no tiene familia, mostrar solo Grupos para que pueda crear una
    if (hasFamily === false) {
      return [
        {
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          ),
          name: t('common.gestion'),
          subItems: [
            { name: t('common.grupos'), path: "/groups/groups", pro: false },
          ],
        },
      ];
    }
    
    // Mostrar gestión si:
    // 1. Es owner/admin según useMemberRole (con activeFamily disponible)
    // 2. O según userRole del documento (funciona sin activeFamily)
    // 3. O si userDoc.isOwner/isAdmin están establecidos (post-onboarding)
    const shouldShowGestion = 
      isOwnerOrAdmin || 
      userRole === 'owner' || 
      userRole === 'admin' || 
      userDoc?.isOwner === true || 
      userDoc?.isAdmin === true ||
      userDoc?.role === 'admin';
    
    
    
    if (!shouldShowGestion) {
      // Members no ven gestión
      
      return [];
    }
    // Owner/Admin ven todo
    return [
      {
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
        name: t('common.gestion'),
        subItems: [
          { name: t('common.grupos'), path: "/groups/groups", pro: false },
          { name: copys.members, path: "/groups/members", pro: false },
          { name: copys.activities, path: "/groups/activities", pro: false },
          { name: copys.tasks, path: "/groups/checklist", pro: false },
        ],
      },
    ];
  }, [isOwnerOrAdmin, userRole, userDoc, memberRoleLoading, userDocLoading, copys, canAccessTemplates, canAccessEvaluations, isEducacion]);

  // Eliminado: cuentaItems ya no se necesita

  // Removed: Consolidated with the useEffect above

  const isSuperAdmin = useMemo(() => userDoc?.role === "superadmin", [userDoc?.role]);

  const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(new Set());
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>({});
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const isActive = useCallback(
    (path: string) => location.pathname === path,
    [location.pathname]
  );

  // Initialize open submenus
  useEffect(() => {
    if (isMobileOpen) {
      const allKeys = new Set<string>();
      const menuGroups = [
        { type: "dashboard", items: dashboardItems },
        { type: "gestion", items: organizacionItems },
      ];
      menuGroups.forEach(({ type, items }) => {
        items.forEach((nav, index) => {
          if (nav.subItems) allKeys.add(`${type}-${index}`);
        });
      });
      
      // Solo actualizar si el contenido cambió
      setOpenSubmenus(prev => {
        if (prev.size !== allKeys.size) return allKeys;
        for (const key of allKeys) {
          if (!prev.has(key)) return allKeys;
        }
        return prev;
      });
    } else {
      // Cerrar todos los submenús cuando está colapsado
      setOpenSubmenus(prev => prev.size === 0 ? prev : new Set());
    }
  }, [isMobileOpen, isMember]);

  // Calculate submenu heights
  useEffect(() => {
    if (openSubmenus.size === 0) {
      setSubMenuHeight({});
      return;
    }

    let timeoutId: NodeJS.Timeout | null = null;
    
    const calculateHeights = () => {
      const newHeights: Record<string, number> = {};
      openSubmenus.forEach((key) => {
        const element = subMenuRefs.current[key];
        if (element) {
          element.style.height = "auto";
          const height = element.scrollHeight;
          element.style.height = "";
          newHeights[key] = height;
        }
      });
      
      // Solo actualizar si hay cambios reales
      setSubMenuHeight(prev => {
        const hasChanges = Object.keys(newHeights).some(key => prev[key] !== newHeights[key]) ||
                          Object.keys(prev).length !== Object.keys(newHeights).length;
        return hasChanges ? newHeights : prev;
      });
    };

    // Calcular alturas iniciales con un pequeño delay
    timeoutId = setTimeout(() => {
      calculateHeights();
    }, 50);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [openSubmenus]);

  const handleSubmenuToggle = (index: number, menuType: string) => {
    const key = `${menuType}-${index}`;
    setOpenSubmenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) newSet.delete(key);
      else newSet.add(key);
      return newSet;
    });
  };

  const renderMenuItems = (items: NavItem[], menuType: string) => (
    <ul className="flex flex-col gap-2">
      {items.map((nav, index) => (
        <li key={nav.name}>
          {nav.subItems ? (
            <>
              <button
                type="button"
                onClick={() => handleSubmenuToggle(index, menuType)}
                aria-expanded={openSubmenus.has(`${menuType}-${index}`)}
                aria-controls={`submenu-${menuType}-${index}`}
                className="menu-item group menu-item-inactive cursor-pointer lg:justify-start"
              >
                <span
                  className={`menu-item-icon-size menu-item-icon-inactive`}
                >
                  {nav.icon}
                </span>
                {isMobileOpen && (
                  <span className="menu-item-text">{nav.name}</span>
                )}
                {isMobileOpen && (
                  <ChevronDownIcon
                    className={`ml-auto w-5 h-5 transition-transform duration-200 text-black ${
                      openSubmenus.has(`${menuType}-${index}`) ? "rotate-180" : ""
                    }`}
                  />
                )}
              </button>
              <div
                ref={(el) => {
                  subMenuRefs.current[`${menuType}-${index}`] = el;
                }}
                id={`submenu-${menuType}-${index}`}
                className="overflow-hidden transition-all duration-300"
                style={{
                  height: openSubmenus.has(`${menuType}-${index}`)
                    ? `${subMenuHeight[`${menuType}-${index}`]}px`
                    : "0px",
                }}
              >
                <ul className="mt-1 space-y-0.5 ml-9">
                  {nav.subItems.map((subItem) => {
                    let pathToUse = subItem.path;
                    let enabled = true;
                    if (familyMode) {
                      const allowedInFamilyMode = new Set([
                        "/groups/home-groups",
                        "/groups/calendar",
                        "/family/meal-planner",
                        "/family/shopping-list",
                      ]);
                      enabled = allowedInFamilyMode.has(subItem.path);
                      if (enabled && subItem.path === "/groups/home-groups") pathToUse = "/groups/home-groups";
                    } else {
                      enabled = true;
                    }
                    return (
                      <li key={subItem.name}>
                        {enabled ? (
                          <Link
                            to={pathToUse}
                            onClick={handleMobileLinkClick}
                            className={`menu-dropdown-item ${
                              isActive(pathToUse)
                                ? "menu-dropdown-item-active"
                                : "menu-dropdown-item-inactive"
                            }`}
                          >
                            {subItem.name}
                          </Link>
                        ) : (
                          <div className="menu-dropdown-item menu-dropdown-item-inactive opacity-60 cursor-not-allowed select-none flex items-center gap-2">
                            <span>{subItem.name}</span>
                            <LockIcon className="ml-auto w-4 h-4 text-gray-400" />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          ) : (
            nav.path && (
              <Link
                to={nav.path}
                onClick={handleMobileLinkClick}
                className={`menu-item group ${
                  isActive(nav.path) ? "menu-item-active" : "menu-item-inactive"
                }`}
              >
                <span
                  className={`menu-item-icon-size ${
                    isActive(nav.path)
                      ? "menu-item-icon-active"
                      : "menu-item-icon-inactive"
                  }`}
                >
                  {nav.icon}
                </span>
                {isMobileOpen && (
                  <span className="menu-item-text">{nav.name}</span>
                )}
              </Link>
            )
          )}
        </li>
      ))}
    </ul>
  );

  // Detectar si es móvil para optimizar sidebar
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 1366; // nb breakpoint
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1366);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Ref para detectar swipe down
  const touchStartY = useRef<number | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Handler para swipe down para cerrar
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (touchStartY.current === null) return;
    const deltaY = e.touches[0].clientY - touchStartY.current;
    // Si el usuario hace swipe down más de 100px y está en el top del scroll
    if (deltaY > 100 && modalRef.current && modalRef.current.scrollTop === 0) {
      touchStartY.current = null;
    }
  }, [closeMobileSidebar]);

  const handleTouchEnd = useCallback(() => {
    touchStartY.current = null;
  }, []);

  // Función para renderizar el modal móvil
    const renderMobileModal = () => {
      if (!shouldRenderMobile) return null;
    // Profile info para el header
    const displayName = userDoc?.name || currentMemberData?.name || t('common.user');
    const avatarUrl = userDoc?.avatar || currentMemberData?.avatar || null;
    
    const profile = {
      name: displayName,
      email: user?.email || '',
      photo: avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=6366f1&color=fff`,
      role: isOwnerOrAdmin ? (t('common.administrador', { defaultValue: 'Administrador' }) as string) : (t('common.miembro', { defaultValue: 'Miembro' }) as string)
    };

    // Iconos SVG para el menú móvil - Centralizados
    const icons = {
      notifications: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>,
      preferences: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
      security: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
      dashboard: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>,
      board: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>,
      groups: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
      members: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>,
      activities: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
      tasks: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>,
      subscription: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>,
      templates: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>,
      rewards: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>,
      calendar: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
      ai: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 01-2 2h-2a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>,
      evaluations: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
      attendance: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
      reports: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
      logout: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>,
    };

    // ========== MAPEO DE RUTAS A FEATURES ==========
    const routeToFeatureMap: Record<string, { feature: string | null; name: string; description: string }> = {
      '/rewards/manage': { feature: 'rewards', name: t('common.recompensas', { defaultValue: 'Recompensas' }) as string, description: t('sidebar.feature.rewardsDesc', { defaultValue: 'Gestiona el sistema de recompensas y puntos familiar' }) as string },
      '/family/recipes': { feature: 'recipes', name: t('family.recipes', { defaultValue: 'Recetas' }) as string, description: t('sidebar.feature.recipesDesc', { defaultValue: 'Accede a todas tus recetas familiares compartidas' }) as string },
      '/family/meal-planner': { feature: 'mealPlans', name: t('family.mealPlanner', { defaultValue: 'Planificador de Comidas' }) as string, description: t('sidebar.feature.mealPlannerDesc', { defaultValue: 'Organiza y planifica las comidas semanales de tu familia' }) as string },
      '/family/shopping-list': { feature: 'shoppingLists', name: t('family.shoppingList', { defaultValue: 'Lista de Compras' }) as string, description: t('sidebar.feature.shoppingListDesc', { defaultValue: 'Crea y gestiona listas de compras compartidas' }) as string },
      '/groups/activities': { feature: 'activities', name: t('common.actividades', { defaultValue: 'Actividades' }) as string, description: t('sidebar.feature.activitiesDesc', { defaultValue: 'Gestiona hábitos y actividades del grupo' }) as string },
      '/groups/checklist': { feature: 'checklists', name: t('common.tareas', { defaultValue: 'Tareas' }) as string, description: t('sidebar.feature.tasksDesc', { defaultValue: 'Organiza tareas y checklists grupales' }) as string },
      '/ai-assistant': { feature: null, name: t('sidebar.aiAssistant', { defaultValue: 'Asistente IA' }) as string, description: t('sidebar.feature.aiAssistantDesc', { defaultValue: 'Chat inteligente para consejos de crianza positiva y apoyo familiar.' }) as string },
    };

    // ========== SECCIÓN PRINCIPAL ==========
    type MenuItem = { path: string; icon: React.ReactNode; label: string; badge?: number; section?: string; className?: string; onClick?: () => void };
    const mainItems: MenuItem[] = [];

    // Tablero Personal
    // - Owner/Admin lo ven siempre fuera de modo grupo.
    // - Si NO tiene familia, también debe verlo para poder empezar (y desde ahí crear su primer grupo).
    if (!familyMode && (isOwnerOrAdmin || hasFamily === false)) {
      mainItems.push({ path: '/groups/dashboard', icon: icons.dashboard, label: t('common.tableropersonal'), badge: pendingRedemptionsCount });
    }
    
    // Tablero Grupal
    // Si no tiene familia, no mostrar este acceso (evita enviar a una pantalla sin data).
    if (hasFamily !== false) {
      mainItems.push({ path: '/groups/home-groups', icon: icons.board, label: t('common.tablerogrupal') });
    }
    
    // Asistente IA (solo fuera de modo grupo y si feature está habilitada)
    if (!familyMode && features?.gritiAI?.enabled) {
      mainItems.push({ path: '/ai-assistant', icon: icons.ai, label: t('features.gritiAI.sidebarLabel', { defaultValue: 'Asistente IA' }) as string });
    }
    
    // Calendario grupal (solo en modo grupo)
    if (familyMode) {
      mainItems.push({ path: '/groups/calendar', icon: icons.calendar, label: t('common.calendario', { defaultValue: 'Calendario' }) as string });
    }
    
    // Planificador de comidas (solo en modo grupo y familias)
    if (familyMode && activeFamily?.licenseType === 'familia') {
      mainItems.push({ path: '/family/meal-planner', icon: <CalendarDays className="w-6 h-6" />, label: t('family.mealPlanner', { defaultValue: 'Planificador de Comidas' }) as string });
    }


    // Lista de compras (solo en modo grupo, familias y si feature está habilitada)
    if (familyMode && activeFamily?.licenseType === 'familia' && features?.shoppingList?.enabled) {
      mainItems.push({ path: '/family/shopping-list', icon: <ShoppingCart className="w-6 h-6" />, label: t('family.shoppingList', { defaultValue: 'Lista de Compras' }) as string });
    }

    // ========== SECCIÓN GESTIÓN (solo admin, fuera de modo grupo) ==========
    const gestionItems: MenuItem[] = [];

    // Si no tiene familia, permitir ver "Grupos" para crear su primer grupo.
    if (!familyMode && hasFamily === false) {
      gestionItems.push({ path: '/groups/groups', icon: icons.groups, label: t('common.grupos') });
    } else if (isOwnerOrAdmin && !familyMode) {
      gestionItems.push({ path: '/groups/groups', icon: icons.groups, label: t('common.grupos') });
      gestionItems.push({ path: '/groups/members', icon: icons.members, label: copys.members });
      gestionItems.push({ path: '/groups/activities', icon: icons.activities, label: copys.activities });
      gestionItems.push({ path: '/groups/checklist', icon: icons.tasks, label: copys.tasks });
      gestionItems.push({ path: '/groups/routines', icon: icons.calendar, label: t('common.rutinas', { defaultValue: 'Rutinas' }) as string });
      
      // EDUCACIÓN: Agregar items específicos
      if (canAccessEvaluations) {
        gestionItems.push({ path: '/groups/subjects', icon: icons.templates, label: t('educa.subjects', { defaultValue: 'Materias' }) as string });
        gestionItems.push({ path: '/groups/evaluations', icon: icons.evaluations, label: t('educa.evaluations', { defaultValue: 'Evaluaciones' }) as string });
        gestionItems.push({ path: '/groups/attendance', icon: icons.attendance, label: t('educa.attendance', { defaultValue: 'Asistencia' }) as string });
        gestionItems.push({ path: '/groups/reports', icon: icons.reports, label: t('educa.reports', { defaultValue: 'Reportes' }) as string });
      }
    } else if (canAccessEvaluations && !familyMode) {
      // ESTUDIANTES: Solo ver sus calificaciones
      gestionItems.push({ path: '/groups/my-grades', icon: icons.evaluations, label: t('educa.myGrades', { defaultValue: 'Mis Calificaciones' }) as string });
    }

    // ========== SECCIÓN HERRAMIENTAS (fuera de modo grupo) ==========
    const toolsItems: MenuItem[] = [];
    if (!familyMode) {
      // Calendario - solo si feature está habilitada
      if (features?.calendar?.enabled) {
        toolsItems.push({ path: '/calendar', icon: icons.calendar, label: t('common.calendario') });
      }
      
      // Templates - OCULTO (biblioteca pausada)
      // if (isOwnerOrAdmin && canAccessTemplates && templateLibraryEnabled && features?.templateLibrary?.enabled) {
      //   toolsItems.push({ path: '/templates', icon: icons.templates, label: t('common.rutinas') });
      // }
      
      // Recompensas (solo admin, familias, si está habilitado)
      if (isOwnerOrAdmin && marketplaceEnabled && !isEducacion) {
        toolsItems.push({ path: '/rewards/manage', icon: icons.rewards, label: t('common.recompensas'), badge: pendingRedemptionsCount });
      }
      
      // Cocina (solo familias)
      if (activeFamily?.licenseType === 'familia') {
        // Recetas - solo si feature está habilitada
        if (features?.advancedRecipes?.enabled) {
          toolsItems.push({ path: '/family/recipes', icon: <ChefHat className="w-6 h-6" />, label: t('family.recipes', { defaultValue: 'Recetas' }) as string });
        }
        
        // Planificador - solo si feature está habilitada
        if (features?.mealPlanCalendar?.enabled) {
          toolsItems.push({ path: '/family/meal-planner', icon: <CalendarDays className="w-6 h-6" />, label: t('family.mealPlannerShort', { defaultValue: 'Planificador' }) as string });
        }
        
        // Lista de compras - solo si feature está habilitada
        if (features?.shoppingList?.enabled) {
          toolsItems.push({ path: '/family/shopping-list', icon: <ShoppingCart className="w-6 h-6" />, label: t('family.shoppingList', { defaultValue: 'Lista de Compras' }) as string });
        }
      }
    }

    // ========== SECCIÓN CONFIGURACIÓN (fuera de modo grupo) ==========
    const configItems: MenuItem[] = [];
    if (!familyMode) {
      configItems.push({ path: '/notifications', icon: icons.notifications, label: t('common.notificaciones'), badge: unreadCount });
      
      // Personalización - solo mobile/tablet (en desktop está en user dropdown)
      configItems.push({ 
        path: '/settings/personalization', 
        icon: <Palette className="w-5 h-5" />,
        label: t('settings.personalization', { defaultValue: 'Personalización' }) as string,
        className: 'tablet-13:hidden'
      });

      configItems.push({
        path: '/terms',
        icon: <FileText className="w-5 h-5" />,
        label: t('legal.terms', { defaultValue: 'Términos y condiciones' }) as string,
        className: 'tablet-13:hidden'
      });

      configItems.push({
        path: '/privacy',
        icon: <Shield className="w-5 h-5" />,
        label: t('legal.privacy', { defaultValue: 'Política de Privacidad' }) as string,
        className: 'tablet-13:hidden'
      });
      
      configItems.push({ path: '/preferences', icon: icons.preferences, label: t('common.preferencias') });
      
      if (isOwnerOrAdmin) {
        configItems.push({ path: '/subscription', icon: icons.subscription, label: t('common.suscripcion') });
      }
    } else {
      // En modo grupo, solo notificaciones
      configItems.push({ path: '/notifications', icon: icons.notifications, label: t('common.notificaciones'), badge: unreadCount });
    }
    
    // ========== BOTÓN SOPORTE (siempre visible, fuera de modo grupo) ==========
    if (!familyMode) {
    }

    // Componente reutilizable para renderizar items
    const MenuItem = ({ item }: { item: MenuItem }) => {
      const featureConfig = routeToFeatureMap[item.path];
      const isBlocked = featureConfig?.feature ? isFeatureBlocked(featureConfig.feature as any) : false;
      const active = isActive(item.path);

      // Mapeo de colores para item activo
      const getActiveColor = (path: string) => {
        if (path === '/groups/dashboard') return 'bg-[#AAD08C] text-white';
        if (path === '/groups/home-groups') return 'bg-[#FDBC52] text-gray-900';
        if (path === '/ai-assistant') return 'bg-[#055590] text-white';
        if (path === '/groups/calendar' || path === '/calendar') return 'bg-[#EF6E6B] text-white';
        if (path.startsWith('/rewards')) return 'bg-[#EF6E6B] text-white';
        return 'bg-[#055590] text-white';
      };

      const activeClasses = active ? getActiveColor(item.path) : 'text-gray-900 dark:text-white';
      const iconActiveClasses = active ? 'text-current' : 'text-gray-600 dark:text-gray-400';
      
      const handleClick = () => {
        // Si tiene onClick personalizado, usarlo
        if (item.onClick) {
          item.onClick();
          return;
        }
        
        // Si el path es '#' y no tiene onClick, abrir modal de llamadas por defecto
        if (item.path === '#') {
          openCalls();
          return;
        }
        
        // Si está bloqueada, mostrar modal
        if (isBlocked && featureConfig) {
          checkFeatureAccess({
            feature: featureConfig.feature as any,
            featureName: featureConfig.name,
            description: featureConfig.description
          });
          return;
        }
        
        // Si es AI assistant, verificar límite especial
        if (item.path === '/ai-assistant') {
          const aiLimit = (planInfo?.limits as any)?.aiAssistantConversationsPerMonth || (isTrialActive ? 30 : 0);
          if ((aiLimit === 0 || aiLimit === -1) && !isTrialActive) {
            checkFeatureAccess({
              feature: 'rewards' as any,
              featureName: 'Asistente de Crianza IA',
              description: 'Chat inteligente para consejos de crianza positiva y apoyo familiar.'
            });
            return;
          }
        }
        
        // Navegar normalmente
        navigateToPath(item.path);
      };
      
      const isPremiumPath = ['/ai-assistant', '/recipes', '/group-goals'].includes(item.path);

      return (
        <button
          onClick={handleClick}
          className={`flex items-center gap-4 px-4 py-3 mx-4 my-1 transition-colors active:bg-gray-100 dark:active:bg-gray-700 relative rounded-xl ${activeClasses} ${item.className || ''}`}
        >
          <span className={`w-7 h-7 flex items-center justify-center ${iconActiveClasses} leading-none overflow-visible`}>{item.icon}</span>
          <span className="flex-1 text-left font-black">{item.label}</span>
          
          {/* Badge de notificaciones */}
          {item.badge !== undefined && item.badge > 0 && (
            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] text-center">
              {item.badge > 99 ? '99+' : item.badge}
            </span>
          )}
          
          {/* FeatureBadge SOLO SI ESTÁ BLOQUEADO */}
          {isBlocked && (
            <div className="flex items-center gap-1.5">
              <FeatureBadge variant="blocked" size="sm" />
              <span className="text-[10px] text-red-600 dark:text-red-400 font-medium">{t('subscription.changePlan', { defaultValue: 'Cambiar plan' })}</span>
            </div>
          )}

          {/* Badge Premium durante trial */}
          {!isBlocked && isTrialActive && isPremiumPath && (
            <span className="px-2 py-0.5 text-[10px] font-black rounded-full bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 flex items-center gap-1">
              👑 Premium
            </span>
          )}
          
          <svg className="w-5 h-5 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      );
    };

    // Componente para sección con título
    const MenuSection = ({ title, items }: { title?: string; items: MenuItem[] }) => {
      if (items.length === 0) return null;
      return (
        <div className="py-1">
          {title && (
            <div className="px-6 py-2">
              <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{title}</span>
            </div>
          )}
          {items.map((item, idx) => <MenuItem key={idx} item={item} />)}
        </div>
      );
    };

    return (
      <>
        {/* Overlay con tap para cerrar */}
        <div
          className={`fixed inset-0 bg-black/50 z-[9999] backdrop-blur-sm nb:hidden transition-opacity duration-300 ease-out ${
            isAnimatingIn || isAnimatingOut ? 'opacity-0' : 'opacity-100'
          } ${isAnimatingOut ? 'pointer-events-none' : ''}`}
          onClick={closeMobileSidebar}
        />

        {/* Modal Full-Screen con animación desde la derecha */}
        <div
          ref={modalRef}
          className={`fixed inset-0 z-[9999] nb:hidden flex flex-col overflow-hidden transition-transform duration-300 ease-out ${
            isAnimatingIn || isAnimatingOut ? 'translate-x-full' : 'translate-x-0'
          } ${isAnimatingOut ? 'pointer-events-none' : ''}`}
          style={{ backgroundColor: 'white', paddingBottom: 'env(safe-area-inset-bottom)' }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Handle bar para indicar swipe */}
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 bg-gray-300 dark:bg-gray-700 rounded-full" />
          </div>

          {/* Header con perfil */}
          <div
            className="px-5 pb-5 pt-3 relative"
            style={{ backgroundColor: '#055590', paddingTop: 'calc(env(safe-area-inset-top) + 12px)' }}
          >
            <div className="absolute inset-x-0 top-0 -translate-y-full h-[env(safe-area-inset-top)] bg-gradient-to-br from-brand-400 via-brand-500 to-brand-600 dark:from-brand-700 dark:via-brand-800 dark:to-brand-900" />
            {/* Botón cerrar X */}
            <button
              onClick={closeMobileSidebar}
              className="absolute right-3 top-3 w-9 h-9 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-2xl transition-colors"
            >
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Profile clickable - oculto en modo grupo */}
            {!familyMode ? (
              <button
                onClick={() => navigateToPath('/profile')}
                className="w-full flex items-center gap-3 mb-4 group text-left"
              >
                <img 
                  src={profile.photo} 
                  alt={profile.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-white/40 group-hover:border-white/60 transition-colors"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-white truncate">{profile.name}</h3>
                  <p className="text-sm text-white/70 group-hover:text-white/90 transition-colors">{t('common.viewProfile', { defaultValue: 'Ver perfil' })} ›</p>
                </div>
              </button>
            ) : (
              <div className="w-full flex items-center gap-3 mb-4 text-left">
                <div className="w-12 h-12 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center">
                  <span className="text-2xl">👨‍👩‍👧‍👦</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-white truncate">{activeFamily?.name || t('common.family', { defaultValue: 'Familia' })}</h3>
                  <p className="text-sm text-white/70">{t('common.groupMode', { defaultValue: 'Modo Grupo' })}</p>
                </div>
              </div>
            )}

            {/* Family Switcher + Modo Grupo */}
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <FamilySwitcher />
              </div>
              {!familyMode ? (
                <button
                  onClick={() => { enableFamilyMode(); navigateToPath('/groups/home-groups'); }}
                  className="px-4 py-2 bg-[#FDBC52] hover:bg-[#FDBC52]/90 text-gray-900 font-black text-sm rounded-xl shadow-sm whitespace-nowrap border border-black/5"
                >
                  👪 {t('common.modoGrupo', { defaultValue: 'Modo Grupo' })}
                </button>
              ) : (
                <button
                  onClick={() => { openExitModal(); closeMobileSidebar(); }}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-black text-sm rounded-xl shadow-sm whitespace-nowrap border border-white/10"
                >
                  🚪 {t('common.salir')}
                </button>
              )}
            </div>
          </div>

          {/* Lista de opciones con scroll */}
          <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-900 min-h-0 scrollbar-hide">
            {/* Sección Principal */}
            <MenuSection items={mainItems} />
            
            {/* Divider si hay gestión */}
            {gestionItems.length > 0 && <div className="h-px bg-gray-100 dark:bg-gray-800 mx-4" />}
            
            {/* Sección Gestión */}
            <MenuSection title={t('common.gestion', 'Gestión')} items={gestionItems} />
            
            {/* Divider si hay herramientas */}
            {toolsItems.length > 0 && <div className="h-px bg-gray-100 dark:bg-gray-800 mx-4" />}
            
            {/* Sección Herramientas */}
            <MenuSection title={t('sidebar.tools', { defaultValue: 'Herramientas' })} items={toolsItems} />
            
            {/* Divider antes de configuración */}
            <div className="h-px bg-gray-100 dark:bg-gray-800 mx-4" />
            
            {/* Sección Configuración */}
            <MenuSection title={t('sidebar.settings', { defaultValue: 'Configuración' })} items={configItems} />
            
            {/* Divider antes de soporte */}
            <div className="h-px bg-gray-100 dark:bg-gray-800 mx-4" />

            {/* Contactar Soporte */}
            <div className="py-2 px-4">
              <button
                onClick={() => { setShowContactSupport(true); closeMobileSidebar(); }}
                className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className="font-black">{t('sidebar.contactSupport', { defaultValue: 'Contactar Soporte' })}</span>
              </button>
            </div>

            {/* Divider antes de logout */}
            <div className="h-2 bg-gray-100 dark:bg-gray-800" />

            {/* Logout/Salir */}
            <div className="py-2 px-4">
              {familyMode ? (
                <button
                  onClick={() => { openExitModal(); closeMobileSidebar(); }}
                  className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="font-black">Salir de Modo Grupo</span>
                </button>
              ) : (
                <button
                  onClick={async () => {
                    try {
                      setIsSigningOut(true);
                      try {
                        localStorage.setItem('force_signout', '1');
                      } catch {
                      }
                      // Limpiar estado local sin borrar force_signout (necesario para native)
                      try {
                        localStorage.removeItem('session_start_time');
                        localStorage.removeItem('firestore_cache_clear_version');
                      } catch {
                      }
                      sessionStorage.clear();
                      try {
                        const platform = Capacitor.getPlatform();
                        if (platform === 'ios') {
                          const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
                          await FirebaseAuthentication.signOut();
                        }
                      } catch {
                      }
                      await signOut(auth);
                      closeMobileSidebar();
                      navigate('/signin');
                    } catch (error) {
                      console.error('Error al cerrar sesión:', error);
                    } finally {
                      setIsSigningOut(false);
                    }
                  }}
                  className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  {icons.logout}
                  <span className="font-black">{t('common.cerrarsesion', 'Cerrar sesión')}</span>
                </button>
              )}
            </div>
            
            {/* Espacio extra para scroll suave */}
            <div className="h-4" />
          </div>
        </div>
      </>
    );
  };

  // El aside tradicional no se renderiza en móvil (usamos el modal full-screen)
  // En modo grupo desktop, siempre renderizamos el mini-sidebar
  const shouldRenderAside = !isMobile;

  return (
    <>
      {isSigningOut && (
        <div className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-2xl">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto mb-3"></div>
              <p className="text-gray-700 dark:text-gray-300">{t('common.cerrandoSesion', 'Cerrando sesión…')}</p>
            </div>
          </div>
        </div>
      )}
      {/* Modal móvil full-screen con animación */}
      {isMobile && renderMobileModal()}

      {/* Sidebar tradicional - desktop/tablet siempre visible */}
      {shouldRenderAside && (
      <aside
        className="fixed hidden nb:flex flex-col transition-all duration-300 ease-in-out z-20 nb:w-60 2xl:w-80 top-0 left-0 bottom-0 nb:bottom-0 nb:left-0 nb:h-auto nb:rounded-none nb:shadow-lg text-gray-900 dark:text-white bg-white dark:bg-gray-900"
        role={isMobileOpen ? "dialog" : undefined}
        aria-modal={isMobileOpen ? true : undefined}
        aria-label={t('common.navegacionlateral')}
      >
      {/* Espaciador superior para compensar el header */}
      <div className="pt-20" />

      <div className="flex-1 flex flex-col overflow-hidden">
        <nav className="flex-1 flex flex-col justify-between pb-4 overflow-y-auto overflow-x-hidden scrollbar-hide px-3">
          {/* Vista desktop - items horizontales con emojis */}
          <div className={`flex flex-col ${familyMode ? 'gap-1' : 'gap-1'}`}>
              {/* Modo Grupo - visible solo desktop fuera de modo grupo */}
              {!familyMode && hasFamily !== false && (
                <button
                  onClick={() => {
                    enableFamilyMode();
                    navigate("/groups/home-groups", { replace: true });
                  }}
                  className="flex items-center gap-3 w-full px-3 py-3 rounded-2xl transition-all duration-200 bg-[#FDBC52] hover:bg-[#FDBC52]/90 text-gray-900 font-black shadow-sm mb-2 border border-black/5"
                >
                  <span className="text-xl">👪</span>
                  <span className="text-sm whitespace-nowrap">{t('common.modoGrupo', { defaultValue: 'Modo Grupo' })}</span>
                </button>
              )}

              {(isOwnerOrAdmin || hasFamily === false) && !familyMode && (
                <Link
                  to="/groups/dashboard"
                  onClick={handleMobileLinkClick}
                  className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                    isActive("/groups/dashboard") 
                      ? 'bg-[#AAD08C] text-white shadow-sm' 
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                  }`}
                >
                  <svg className={`w-5 h-5 ${isActive("/groups/dashboard") ? 'text-white' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-sm font-black whitespace-nowrap">{t('sidebar.panel', { defaultValue: 'Panel' })}</span>
                </Link>
              )}

              {/* Inicio grupal - solo si tiene familia */}
              {hasFamily !== false && (
                <Link
                  to="/groups/home-groups"
                  onClick={handleMobileLinkClick}
                  className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                    isActive("/groups/home-groups") 
                      ? 'bg-[#FDBC52] text-gray-900 shadow-sm' 
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                  }`}
                >
                  <svg className={`w-5 h-5 ${isActive("/groups/home-groups") ? 'text-gray-900' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  <span className="text-sm font-black whitespace-nowrap">{t('sidebar.familyDashboard', { defaultValue: 'Tablero Grupal' })}</span>
                </Link>
              )}

              {/* Asistente IA - solo fuera de modo grupo */}
              {!familyMode && features?.gritiAI?.enabled && (
                <Link
                  to="/ai-assistant"
                  onClick={handleMobileLinkClick}
                  className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                    isActive("/ai-assistant") 
                      ? 'bg-[#055590] text-white shadow-sm' 
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                  }`}
                >
                  <svg className={`w-5 h-5 ${isActive("/ai-assistant") ? 'text-white' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 01-2 2h-2a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  <span className="text-sm font-black whitespace-nowrap">{t('features.gritiAI.sidebarLabel', { defaultValue: 'Asistente IA' })}</span>
                </Link>
              )}
              
              {/* Calendario grupal - solo en modo grupo Y si está habilitado */}
              {familyMode && features?.calendar?.enabled && (
                <Link
                  to="/groups/calendar"
                  onClick={handleMobileLinkClick}
                  className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                    isActive("/groups/calendar") 
                      ? 'bg-[#EF6E6B] text-white shadow-sm' 
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                  }`}
                >
                  <svg className={`w-5 h-5 ${isActive("/groups/calendar") ? 'text-white' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm font-black whitespace-nowrap">{t('common.calendario', { defaultValue: 'Calendario' })}</span>
                </Link>
              )}
              
              {/* Planificador de comidas - solo en modo grupo, familias Y si está habilitado */}
              {familyMode && activeFamily?.licenseType === 'familia' && features?.mealPlanCalendar?.enabled && (
                <Link
                  to="/family/meal-planner"
                  onClick={handleMobileLinkClick}
                  className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                    isActive("/family/meal-planner") 
                      ? 'bg-[#FDBC52] text-gray-900 shadow-sm' 
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                  }`}
                >
                  <CalendarDays className={`w-5 h-5 ${isActive("/family/meal-planner") ? 'text-gray-900' : 'currentColor'}`} />
                  <span className="text-sm font-black whitespace-nowrap">{t('family.mealPlannerShort', { defaultValue: 'Planificador' })}</span>
                </Link>
              )}

                  {/* Shopping List - solo en modo grupo, familias Y si está habilitado */}
                  {familyMode && activeFamily?.licenseType === 'familia' && features?.shoppingList?.enabled && (
                    <Link
                      to="/family/shopping-list"
                      onClick={handleMobileLinkClick}
                      className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                        isActive("/family/shopping-list") 
                          ? 'bg-[#055590] text-white shadow-sm' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      <ShoppingCart className={`w-5 h-5 ${isActive("/family/shopping-list") ? 'text-white' : 'currentColor'}`} />
                      <span className="text-sm font-black whitespace-nowrap">{t('family.shoppingList', { defaultValue: 'Lista de Compras' })}</span>
                    </Link>
                  )}
              
              {/* Templates - OCULTO (biblioteca pausada)
              {familyMode && activeFamily?.licenseType === 'familia' && features?.templateLibrary?.enabled && (
                <Link
                  to="/templates"
                  onClick={handleMobileLinkClick}
                  className={`flex items-center gap-3 w-full px-3 py-3  rounded-xl transition-all duration-200 ${
                    isActive("/templates") ? 'bg-white/30 text-white shadow-sm' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-white/10'
                  }`}
                >
                  <span className="w-8 h-8 flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                  </span>
                  <span className="text-xs font-medium text-center leading-tight">Plantillas</span>
                </Link>
              )}
              */}
              
              {/* Dashboard Diario - Oculto temporalmente */}
              {/* Dashboard Diario - comentado temporalmente */}

              {(isOwnerOrAdmin || hasFamily === false) && !familyMode && (
                <>
                  <Link
                    to="/groups/groups"
                    onClick={handleMobileLinkClick}
                    className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                      isActive("/groups/groups") 
                        ? 'bg-[#055590] text-white shadow-sm' 
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    <svg className={`w-5 h-5 ${isActive("/groups/groups") ? 'text-white' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span className="text-sm font-black whitespace-nowrap">{t('sidebar.groups', { defaultValue: 'Grupos' })}</span>
                  </Link>

                  {isOwnerOrAdmin && (
                    <Link
                      to="/groups/members"
                      onClick={handleMobileLinkClick}
                      className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                        isActive("/groups/members") 
                          ? 'bg-[#AAD08C] text-white shadow-sm' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      <svg className={`w-5 h-5 ${isActive("/groups/members") ? 'text-white' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <span className="text-sm font-black whitespace-nowrap">{t('sidebar.members', { defaultValue: 'Miembros' })}</span>
                    </Link>
                  )}

                  <Link
                    to="/groups/activities"
                    onClick={handleMobileLinkClick}
                    className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                      isActive("/groups/activities") 
                        ? 'bg-[#FDBC52] text-gray-900 shadow-sm' 
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    <svg className={`w-5 h-5 ${isActive("/groups/activities") ? 'text-gray-900' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span className="text-sm font-black whitespace-nowrap">{t('sidebar.activities', { defaultValue: 'Actividades' })}</span>
                  </Link>

                  <Link
                    to="/groups/checklist"
                    onClick={handleMobileLinkClick}
                    className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                      isActive("/groups/checklist") 
                        ? 'bg-[#EF6E6B] text-white shadow-sm' 
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    <svg className={`w-5 h-5 ${isActive("/groups/checklist") ? 'text-white' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                    <span className="text-sm font-black whitespace-nowrap">{t('sidebar.tasks', { defaultValue: 'Tareas' })}</span>
                  </Link>

                  {/* Rutinas */}
                  {isOwnerOrAdmin && (
                    <Link
                      to="/groups/routines"
                      onClick={handleMobileLinkClick}
                      className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                        isActive("/groups/routines") 
                          ? 'bg-[#AAD08C] text-white shadow-sm' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      <svg className={`w-5 h-5 ${isActive("/groups/routines") ? 'text-white' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm font-black whitespace-nowrap">{t('common.rutinas', { defaultValue: 'Rutinas' })}</span>
                    </Link>
                  )}

                  {/* Materias - Solo Logrit Educa */}
                  {isOwnerOrAdmin && canAccessEvaluations && (
                    <Link
                      to="/groups/subjects"
                      onClick={handleMobileLinkClick}
                      className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                        isActive("/groups/subjects") 
                          ? 'bg-[#055590] text-white shadow-sm' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                      }`}
                    >
                        <svg className={`w-5 h-5 ${isActive("/groups/subjects") ? 'text-white' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    <span className="text-sm font-black whitespace-nowrap">Materias</span>
                  </Link>
                  )}

                  {/* Evaluaciones - Solo Logrit Educa */}
                  {isOwnerOrAdmin && canAccessEvaluations && (
                    <Link
                      to="/groups/evaluations"
                      onClick={handleMobileLinkClick}
                      className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                        isActive("/groups/evaluations") ? 'bg-[#FDBC52] text-gray-900 shadow-sm' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                      }`}
                    >
                        <svg className={`w-5 h-5 ${isActive("/groups/evaluations") ? 'text-gray-900' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    <span className="text-sm font-black whitespace-nowrap">Evaluaciones</span>
                  </Link>
                  )}

                  {/* Asistencia - Solo Logrit Educa */}
                  {isOwnerOrAdmin && canAccessEvaluations && (
                    <Link
                      to="/groups/attendance"
                      onClick={handleMobileLinkClick}
                      className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                        isActive("/groups/attendance") ? 'bg-[#EF6E6B] text-white shadow-sm' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                      }`}
                    >
                        <svg className={`w-5 h-5 ${isActive("/groups/attendance") ? 'text-white' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    <span className="text-sm font-black whitespace-nowrap">Asistencia</span>
                  </Link>
                  )}

                  {/* Boletines - Solo Logrit Educa */}
                  {isOwnerOrAdmin && canAccessEvaluations && (
                    <Link
                      to="/educa/report-cards"
                      onClick={handleMobileLinkClick}
                      className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                        isActive("/educa/report-cards") ? 'bg-[#AAD08C] text-white shadow-sm' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                      }`}
                    >
                        <svg className={`w-5 h-5 ${isActive("/educa/report-cards") ? 'text-white' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    <span className="text-sm font-black whitespace-nowrap">Boletines</span>
                  </Link>
                  )}

                  {/* Reportes - Solo Logrit Educa */}
                  {isOwnerOrAdmin && canAccessEvaluations && (
                    <Link
                      to="/groups/reports"
                      onClick={handleMobileLinkClick}
                      className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                        isActive("/groups/reports") ? 'bg-[#055590] text-white shadow-sm' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                      }`}
                    >
                        <svg className={`w-5 h-5 ${isActive("/groups/reports") ? 'text-white' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    <span className="text-sm font-black whitespace-nowrap">Reportes</span>
                  </Link>
                  )}

                  {/* Comunicaciones - Solo Logrit Educa */}
                  {isOwnerOrAdmin && canAccessEvaluations && (
                    <Link
                      to="/educa/communications"
                      onClick={handleMobileLinkClick}
                      className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                        isActive("/educa/communications") ? 'bg-[#FDBC52] text-gray-900 shadow-sm' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                      }`}
                    >
                        <svg className={`w-5 h-5 ${isActive("/educa/communications") ? 'text-gray-900' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2v-1M5 19H3m6 0H3m6 0H3m6 0v-7h-6m-6 0H9m-6 0H3" />
                      </svg>
                    <span className="text-sm font-black whitespace-nowrap">Comunicación</span>
                  </Link>
                  )}

                  {/* Biblioteca de Templates - OCULTO (biblioteca pausada)
                  {isOwnerOrAdmin && canAccessTemplates && templateLibraryEnabled && features?.templateLibrary?.enabled && (
                    <Link
                      to="/templates"
                      onClick={handleMobileLinkClick}
                      className={`flex items-center gap-3 w-full px-3 py-3  rounded-xl transition-all duration-200 ${
                        isActive("/templates") ? 'bg-white/30 text-white shadow-sm' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-white/10'
                      }`}
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    <span className="text-sm font-bold">Rutinas</span>
                  </Link>
                  )}
                  */}

                  {/* Gestionar Recompensas - Solo Logrit Familia (por ahora) */}
                  {/* Alineado a AdminPlans: rewards = -1 en FREE (bloqueado) */}
                  {isOwnerOrAdmin && marketplaceEnabled && !isEducacion && (
                    isFeatureBlocked('rewards') ? (
                      <button
                        onClick={() => {
                          // NO cerrar sidebar si está bloqueado - el modal es bloqueante
                          promptUpgrade(
                            'Sistema de Recompensas',
                            'Motiva a tu familia con un sistema completo de puntos, recompensas y marketplace.'
                          );
                        }}
                        className={`flex items-center gap-3 w-full px-3 py-2.5  rounded-xl transition-all duration-200 text-gray-700 dark:text-gray-300 opacity-60`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                        </svg>
                        <span className="text-sm font-black whitespace-nowrap">{t('common.recompensas', { defaultValue: 'Recompensas' })}</span>
                      </button>
                    ) : (
                      <Link
                        to="/rewards/manage"
                        onClick={handleMobileLinkClick}
                        className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                          isActive("/rewards/manage") || isActive("/rewards/requests") 
                            ? 'bg-[#EF6E6B] text-white shadow-sm' 
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        <div className="relative w-5 h-5 flex items-center justify-center">
                          <svg className={`w-5 h-5 ${isActive("/rewards/manage") || isActive("/rewards/requests") ? 'text-white' : 'currentColor'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                          </svg>
                          {/* Badge de pendientes */}
                          {pendingRedemptionsCount > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                              {pendingRedemptionsCount > 9 ? '9+' : pendingRedemptionsCount}
                            </span>
                          )}
                        </div>
                        <span className="text-sm font-black whitespace-nowrap">{t('common.recompensas', { defaultValue: 'Recompensas' })}</span>
                      </Link>
                    )
                  )}

                  {/* Recetas - visible si está habilitado */}
                  {activeFamily?.licenseType === 'familia' && features?.advancedRecipes?.enabled && (
                    <Link
                      to="/family/recipes"
                      onClick={handleMobileLinkClick}
                      className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                        isActive("/family/recipes") 
                          ? 'bg-[#055590] text-white shadow-sm' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                      }`}
                    >
                        <ChefHat className={`w-5 h-5 ${isActive("/family/recipes") ? 'text-white' : 'currentColor'}`} />
                        <span className="text-sm font-black whitespace-nowrap">{t('family.recipes', { defaultValue: 'Recetas' })}</span>
                    </Link>
                  )}

                  {/* Planificador de comidas - visible si está habilitado */}
                  {activeFamily?.licenseType === 'familia' && features?.mealPlanCalendar?.enabled && (
                    <Link
                      to="/family/meal-planner"
                      onClick={handleMobileLinkClick}
                      className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                        isActive("/family/meal-planner") 
                          ? 'bg-[#FDBC52] text-gray-900 shadow-sm' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                      }`}
                    >
                        <CalendarDays className={`w-5 h-5 ${isActive("/family/meal-planner") ? 'text-gray-900' : 'currentColor'}`} />
                        <span className="text-sm font-black whitespace-nowrap">{t('family.mealPlannerShort', { defaultValue: 'Planificador' })}</span>
                    </Link>
                  )}

                  {/* Lista de compras - visible si está habilitado */}
                  {activeFamily?.licenseType === 'familia' && features?.shoppingList?.enabled && (
                    <Link
                      to="/family/shopping-list"
                      onClick={handleMobileLinkClick}
                      className={`flex items-center gap-3 w-full px-3 py-2  rounded-xl transition-all duration-200 ${
                        isActive("/family/shopping-list") 
                          ? 'bg-[#055590] text-white shadow-sm' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100'
                      }`}
                    >
                        <ShoppingCart className={`w-5 h-5 ${isActive("/family/shopping-list") ? 'text-white' : 'currentColor'}`} />
                        <span className="text-sm font-black whitespace-nowrap">{t('family.shoppingList', { defaultValue: 'Lista de Compras' })}</span>
                    </Link>
                  )}
                </>
              )}
              {familyMode && (
                <div className="mt-auto pt-4 border-t border-gray-200 dark:border-gray-800 space-y-3 pb-3">
                  {/* Cambiar Integrante */}
                  <button
                    onClick={() => {
                      localStorage.removeItem('active_member_id');
                      localStorage.removeItem('active_member_name');
                      navigate('/groups/home-groups');
                      handleMobileLinkClick();
                    }}
                    className="flex items-center gap-3 w-full px-3 py-3 rounded-xl transition-all duration-200 text-gray-700 dark:text-gray-300 hover:bg-white/10 w-full"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span className="text-sm font-black">Cambiar</span>
                  </button>
                  
                  {/* Salir del Modo Grupo */}
                  <button
                    onClick={() => {
                      openExitModal();
                      handleMobileLinkClick();
                    }}
                    className="flex items-center gap-3 w-full px-3 py-3 rounded-xl transition-all duration-200 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 w-full"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span className="text-sm font-black">{t('common.exit', { defaultValue: 'Salir' })}</span>
                  </button>
                </div>
              )}

              
              {/* Family Mode CTA removido - ahora está en el header */}
              </div>

          {/* Botón Contactar Soporte - Desktop */}
          <div className="mt-4 px-3 pb-3">
            <button
              onClick={() => setShowContactSupport(true)}
              className="flex items-center gap-3 w-full px-3 py-3 rounded-xl transition-all duration-200 text-brand-600 dark:text-brand-400 hover:bg-brand-100 dark:hover:bg-brand-900/20 w-full"
              title={t('sidebar.contactSupport', { defaultValue: 'Contactar Soporte' })}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="text-sm font-black">{t('sidebar.support', { defaultValue: 'Soporte' })}</span>
            </button>
          </div>
        </nav>
        {/* SidebarWidget removido - sidebar siempre colapsado */}
      </div>
      </aside>
      )}

      {/* Bottom Navigation - Solo mobile y tablets (<nb) */}
      <nav
        className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 nb:hidden z-40 shadow-lg"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {familyMode ? (
          // Modo Grupo: [Familia] [Llamar] [Calendario] [SALIR] [Más]
          (() => {
            const showCalendar = !!features?.calendar?.enabled;
            return (
              <div className="grid grid-cols-5 h-16 sm:h-[72px] w-full">
            {/* Familia */}
            <Link
              to="/groups/home-groups"
              className="flex flex-col items-center justify-center h-full transition-colors relative"
            >
              <svg className={`w-6 h-6 sm:w-7 sm:h-7 ${location.pathname === '/groups/home-groups' ? 'text-[#AAD08C]' : 'text-gray-400 dark:text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="text-[10px] sm:text-xs font-black uppercase tracking-wider mt-1 text-gray-600 dark:text-gray-400">{t('common.family', { defaultValue: 'Familia' })}</span>
            </Link>

            {/* Llamar */}
            <button
              onClick={() => {
                if (isCallsBlocked) {
                  promptUpgrade(
                    'Llamadas',
                    'Accede al sistema de llamadas familiares (Casa / Miembro).'
                  );
                  return;
                }
                setCallModalOpen(true);
              }}
              className="flex flex-col items-center justify-center h-full transition-colors relative"
            >
              <svg className="w-6 h-6 sm:w-7 sm:h-7 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="text-[10px] sm:text-xs font-black uppercase tracking-wider mt-1 text-gray-600 dark:text-gray-400">{t('calls.title', { defaultValue: 'Llamar' })}</span>
            </button>

            {/* Calendario */}
            {showCalendar ? (
              <Link
                to="/groups/calendar"
                className="flex flex-col items-center justify-center h-full transition-colors relative"
              >
                <svg className={`w-6 h-6 sm:w-7 sm:h-7 ${(location.pathname === '/groups/calendar' || location.pathname === '/calendar') ? 'text-[#AAD08C]' : 'text-gray-400 dark:text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-[10px] sm:text-xs font-black uppercase tracking-wider mt-1 text-gray-600 dark:text-gray-400">{t('common.calendario', { defaultValue: 'Calendario' })}</span>
              </Link>
            ) : (
              <div className="flex flex-col items-center justify-center h-full opacity-30">
                <svg className="w-6 h-6 sm:w-7 sm:h-7 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-[10px] sm:text-xs font-black uppercase tracking-wider mt-1 text-gray-300 dark:text-gray-600">{t('common.calendario', { defaultValue: 'Calendario' })}</span>
              </div>
            )}

            {/* SALIR - Reemplaza el FAB */}
            <button
              onClick={openExitModal}
              className="flex flex-col items-center justify-center h-full transition-colors relative"
            >
              <svg className="w-6 h-6 sm:w-7 sm:h-7 text-red-500 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="text-[10px] sm:text-xs font-black uppercase tracking-wider mt-1 text-red-500 dark:text-red-400">{t('common.exit', { defaultValue: 'Salir' })}</span>
            </button>

            {/* Más */}
            <button
              onClick={toggleMobileSidebar}
              className="flex flex-col items-center justify-center h-full transition-colors relative"
            >
              <div className="relative">
                <svg className="w-6 h-6 sm:w-7 sm:h-7 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                {(unreadCount > 0 || pendingRedemptionsCount > 0) && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded-full" />
                )}
              </div>
              <span className="text-[10px] sm:text-xs font-black uppercase tracking-wider mt-1 text-gray-600 dark:text-gray-400">{t('common.moreNav', { defaultValue: 'Más' })}</span>
            </button>
              </div>
            );
          })()
        ) : (
          // Modo Normal: 5 botones con grid uniforme
          <div className="grid grid-cols-5 h-16 sm:h-[72px] max-w-lg sm:max-w-2xl mx-auto px-2 sm:px-4">
            {/* Inicio */}
            <Link
              to="/groups/dashboard"
              className={`flex flex-col items-center justify-center h-full transition-colors relative ${
                location.pathname === '/groups/dashboard'
                  ? 'text-[#AAD08C]'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              <span className="text-[10px] sm:text-xs font-black uppercase tracking-wider mt-1">{t('common.home', { defaultValue: 'Inicio' })}</span>
            </Link>

            {/* Familia */}
            <Link
              to="/groups/home-groups"
              className={`flex flex-col items-center justify-center h-full transition-colors relative ${
                location.pathname === '/groups/home-groups'
                  ? 'text-[#AAD08C]'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="text-[10px] sm:text-xs font-black uppercase tracking-wider mt-1">{t('common.family', { defaultValue: 'Familia' })}</span>
            </Link>

            {/* Botón + Central - contextual */}
            <div className="flex items-center justify-center relative -mt-8">
              <ContextualFAB
                currentPath={location.pathname}
                searchParams={new URLSearchParams(location.search)}
                navigate={navigate}
                className="!pt-0 !pb-0"
              />
            </div>

            {/* Llamar */}
            <button
              onClick={() => {
                if (isCallsBlocked) {
                  promptUpgrade(
                    'Llamadas',
                    'Accede al sistema de llamadas familiares (Casa / Miembro).'
                  );
                  return;
                }
                setCallModalOpen(true);
              }}
              className={`flex flex-col items-center justify-center h-full transition-colors relative text-gray-400 dark:text-gray-500`}
            >
              <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="text-[10px] sm:text-xs font-black uppercase tracking-wider mt-1">{t('calls.title', { defaultValue: 'Llamar' })}</span>
            </button>

            {/* Más - abre sidebar */}
            <button
              onClick={toggleMobileSidebar}
              className="flex flex-col items-center justify-center h-full transition-colors text-gray-400 dark:text-gray-500 relative"
            >
              <div className="relative">
                <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                {(unreadCount > 0 || pendingRedemptionsCount > 0) && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded-full" />
                )}
              </div>
              <span className="text-[10px] sm:text-xs font-black uppercase tracking-wider mt-1">{t('common.moreNav', { defaultValue: 'Más' })}</span>
            </button>
          </div>
        )}
      </nav>

      {/* Modal de Upgrade para features bloqueadas */}
      <UpgradePlanModal
        isOpen={showUpgradeModal}
        onClose={closeUpgradeModal}
        feature={blockedFeatureInfo?.name}
        reason={blockedFeatureInfo?.description}
      />

      {/* Modal de Contactar Soporte */}
      <ContactSupportModal
        isOpen={showContactSupport}
        onClose={() => setShowContactSupport(false)}
      />

      {/* Modal de Llamadas - Para Mobile */}
      <CallModal
        isOpen={callModalOpen}
        onClose={() => setCallModalOpen(false)}
      />
    </>
  );
}

export default AppSidebar;

type MobileMenuButtonProps = {
  icon: string;
  label: string;
  onClick: () => void;
  badge?: number;
};

function MobileMenuButton({ icon, label, onClick, badge }: MobileMenuButtonProps) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center justify-between px-6 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
    >
      <div className="flex items-center gap-3">
        <span className="text-2xl" aria-hidden>{icon}</span>
        <span className="text-base font-medium">{label}</span>
      </div>
      {typeof badge === 'number' && badge > 0 && (
        <span className="px-2 py-0.5 text-xs rounded-full bg-red-500 text-white font-semibold">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}
